import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { formatOffsetDetectionStepValue, OffsetDetectionSteps, offsetDetectionSteps } from 'motion-master-client';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';

import { StepValueDifference } from '@app/models/offset-detection';
import { ConnectionService } from '@app/services/connection.service';
import { DeviceService } from '@app/services/device.service';
import { SettingService } from '@app/services/setting.service';
import { CommutationAngleOffsetElectricalDegreesPipe } from '@app/shared/commutation-angle-offset-electrical-degrees.pipe';
import { DeviceBaseComponent } from '@app/shared/device-base.component';
import { DocFeelingLuckyLinkComponent } from '@app/shared/doc-feeling-lucky-link/doc-feeling-lucky-link.component';
import { FormatOffsetDetectionStepValuePipe } from '@app/shared/format-offset-detection-step-value.pipe';
import { MotionPreventedTooltipDirective } from '@app/shared/motion-prevented-tooltip.directive';
import { ParameterFormComponent } from '@app/shared/parameter-form/parameter-form.component';
import { ParameterValueDisplayPipe } from '@app/shared/parameter-value-display.pipe';
import { ProgressStepsComponent } from '@app/shared/progress-steps/progress-steps.component';
import { DeviceParameter } from '@app/store/device-parameter.model';
import { Device } from '@app/store/device.model';
import { Parameter } from '@app/store/parameter.model';
import { getCurrentValue } from '@app/util';

@Component({
  selector: 'app-offset-detection',
  templateUrl: './offset-detection.component.html',
  imports: [
    AsyncPipe,
    CommutationAngleOffsetElectricalDegreesPipe,
    DocFeelingLuckyLinkComponent,
    FormatOffsetDetectionStepValuePipe,
    FormsModule,
    MotionPreventedTooltipDirective,
    NgbTooltip,
    NgClass,
    NgTemplateOutlet,
    ParameterFormComponent,
    ParameterValueDisplayPipe,
    ProgressStepsComponent,
  ],
})
export class OffsetDetectionComponent extends DeviceBaseComponent implements OnDestroy {
  private connectionService = inject(ConnectionService);
  private deviceService = inject(DeviceService);
  private settingsService = inject(SettingService);
  private toastr = inject(ToastrService);

  protected offsetDetection = new Subscription();

  @ViewChild('pf') pf: ParameterFormComponent;
  @ViewChild('tf') tf: ParameterFormComponent;

  // Persist previous steps when individual steps are run to prevent losing the values of the other steps in the UI
  cachedSteps: OffsetDetectionSteps = structuredClone(offsetDetectionSteps);

  steps$: BehaviorSubject<OffsetDetectionSteps>;
  formattedValueSteps$: Observable<any>;
  running$: BehaviorSubject<boolean>;
  stepValueDifference$: Observable<StepValueDifference[]>;

  hasCommutationEncoder$: Observable<boolean>;
  isMotionPrevented$: Observable<boolean>;

  commutationAngleOffsetParameter$: Observable<DeviceParameter>;
  commutationAngleOffsetValid$: Observable<boolean>;
  commutationOffsetAppliedPercentOfRatedTorqueParameter$: Observable<DeviceParameter>;
  motorPhasesInvertedParameter$: Observable<DeviceParameter>;

  appliedPercentOfRatedTorque$: Observable<DeviceParameter[]>;
  commutationParameters$: Observable<DeviceParameter[]>;
  expertMode$ = this.settingsService.expertMode$;
  isBrakeConfigured$: Observable<boolean>;

  brakeReleaseWaitTimeParameter$: Observable<Parameter> = of({
    description:
      'Time to wait after releasing the brake before proceeding with each step that requires an open brake. Increase if the brake does not fully release within the default period.',
    id: 'brakeReleaseWaitTime',
    index: 0,
    inputType: 'number',
    min: 1,
    name: 'Brake release wait time',
    readAccess: true,
    subindex: 0,
    typeValue: 'uintValue',
    unit: 'ms',
    value: 50,
    writeAccess: true,
    defaultValue: 50,
  });

  brakeEngageWaitTimeParameter$: Observable<Parameter> = of({
    description:
      'Time to wait after engaging the brake before proceeding with each step that requires a closed brake. Increase if the brake does not fully engage within the default period.',
    id: 'brakeEngageWaitTime',
    index: 0,
    inputType: 'number',
    min: 1,
    name: 'Brake engage wait time',
    readAccess: true,
    subindex: 0,
    typeValue: 'uintValue',
    unit: 'ms',
    value: 50,
    writeAccess: true,
    defaultValue: 50,
  });

  brakeWaitTimeParameters$: Observable<Parameter[]> = combineLatest([
    this.brakeReleaseWaitTimeParameter$,
    this.brakeEngageWaitTimeParameter$,
  ]);

  get canDeactivate(): boolean {
    return !getCurrentValue(this.running$);
  }

  get brakeReleaseWaitTime() {
    return this.tf?.values['brakeReleaseWaitTime'] ?? 50;
  }

  get brakeEngageWaitTime() {
    return this.tf?.values['brakeEngageWaitTime'] ?? 50;
  }

  ngOnDestroy(): void {
    this.steps$.complete();
    this.running$.complete();
    this.offsetDetection?.unsubscribe();
    this.deviceService.requestStopDevice(this.device);
  }

  onDeviceChange(device: Device) {
    this.steps$ = new BehaviorSubject(offsetDetectionSteps);
    this.formattedValueSteps$ = this.steps$.asObservable().pipe(
      map((steps) =>
        steps.map((step) => {
          const formattedValue =
            typeof step.value !== 'undefined' ? formatOffsetDetectionStepValue(step.id, +step.value) : undefined;
          return { ...step, value: formattedValue };
        }),
      ),
    );
    this.running$ = new BehaviorSubject(false);
    this.isBrakeConfigured$ = this.deviceService.selectDeviceShowBrakeConfiguration(device);

    this.stepValueDifference$ = this.steps$
      .asObservable()
      .pipe(switchMap((steps) => this.deviceService.selectDeviceOffsetDetectionStepsValueDifferences(device, steps)));

    this.hasCommutationEncoder$ = this.deviceService.selectDeviceHasCommutationEncoder(device);
    this.isMotionPrevented$ = this.deviceService.selectIsMotionPrevented(device);

    this.commutationAngleOffsetParameter$ = this.deviceService.selectDeviceParameter(device, 'commutationAngleOffset');
    this.commutationAngleOffsetValid$ = this.deviceService.selectDeviceCommutationAngleOffsetValid(device);
    this.commutationOffsetAppliedPercentOfRatedTorqueParameter$ = this.deviceService.selectDeviceParameter(
      device,
      'commutationOffsetAppliedPercentOfRatedTorque',
    );
    this.motorPhasesInvertedParameter$ = this.deviceService.selectDeviceParameter(device, 0x2003, 5);

    this.commutationParameters$ = combineLatest([
      this.deviceService.selectDeviceParameters(device, [
        [0x2009, 3], // Measurement method
        [0x2009, 4], // Phasing controller Kp
        [0x2009, 5], // Phasing controller Ki
        [0x2009, 6], // Phasing controller Kd
      ]),
      this.expertMode$,
    ]).pipe(
      map(([parameters, expertMode]) =>
        parameters.map((p) => {
          p.hidden = expertMode === false;
          return p;
        }),
      ),
    );
  }

  onStartOffsetDetection(step?: OffsetDetectionSteps[number]) {
    const stepIds = step ? [step.id] : offsetDetectionSteps.map((s) => s.id);

    this.offsetDetection?.unsubscribe();
    this.running$.next(true);

    this.offsetDetection = this.connectionService.client.request
      .runOffsetDetection(this.device.serialNumber, stepIds, this.brakeReleaseWaitTime, this.brakeEngageWaitTime)
      .pipe(
        finalize(() => {
          this.running$.next(false);
          /**
           * In case the observable is unsubscribed before completion,
           * mark all running steps as failed.
           */
          const steps = getCurrentValue(this.steps$);
          const runningSteps = steps.map((s) => {
            if (s.status === 'running') {
              s.status = 'failed';
            }
            return s;
          });
          this.steps$.next(runningSteps as OffsetDetectionSteps);
        }),
      )
      .subscribe({
        next: (steps) => {
          if (!step) {
            this.cachedSteps = steps;
          } else {
            const stepsDiff = steps.map((s) => (s.status !== 'idle' ? s : undefined));
            this.cachedSteps = [
              ...this.cachedSteps.map((cachedStep, index) => {
                return stepsDiff[index] ? { ...stepsDiff[index] } : cachedStep;
              }),
            ] as OffsetDetectionSteps;
          }
          this.steps$.next(this.cachedSteps);
        },
        error: (error) => {
          this.toastr.error(`Offset detection procedure failed to complete. ${error.message}`, 'Offset Detection', {
            disableTimeOut: true,
          });
        },
        complete: () => {
          this.deviceService.dispatchGetDeviceParameterValuesRequest(
            this.device,
            [
              { index: 0x2001, subindex: 0, loadFromCache: false },
              { index: 0x2003, subindex: 5, loadFromCache: false },
              { index: 0x2009, subindex: 1, loadFromCache: false },
              { index: 0x2009, subindex: 2, loadFromCache: false },
            ],
            5000,
          );

          const steps = getCurrentValue(this.steps$);
          if (steps.some((step) => step.status === 'failed')) {
            this.toastr.warning(
              'Offset Detection procedure completed with some failed steps. Please review the results.',
              'Offset Detection',
            );
          } else {
            this.toastr.success('Offset Detection procedure completed successfully.', 'Offset Detection');
          }
        },
      });
  }

  onChangeCommutationOffsetAppliedPercentOfRatedTorque(value: any) {
    this.deviceService.requestSetDeviceParameterValue(
      this.device,
      'commutationOffsetAppliedPercentOfRatedTorque',
      value,
    );
  }

  onStopOffsetDetection() {
    this.deviceService.requestStopDevice(this.device);
  }

  onApply(controlValue: { [key: string]: any }) {
    if (!this.pf.form.valid) {
      return;
    }
    this.deviceService.requestSetDeviceParameterValues(controlValue);
  }
}
